// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Button, InlineNotification, Tag, Tile } from '@carbon/react';
import type { PublishAction } from '../../../types';

export type TargetDecision = 'approve' | 'reject';

/** Card copy per canonical coverage target (kg, sop, rag, tariff — the
 * service's TARGET_NAMES). Unknown targets fall back to a generic card so
 * new targets render safely before this map learns about them. */
const TARGET_COPY: Record<
  string,
  { title: string; description: string; approveLabel: string; rejectable: boolean }
> = {
  kg: {
    title: 'Knowledge graph',
    description:
      'Site and equipment entities staged as a change set for review; approving merges them into the knowledge graph.',
    approveLabel: 'Approve and merge',
    rejectable: true,
  },
  sop: {
    title: 'Standard operating procedures',
    description:
      'Procedures generated by SOP Factory as held pull requests, labeled with this onboarding session; approving merges them into the site SOP library.',
    approveLabel: 'Approve and merge all',
    rejectable: true,
  },
  rag: {
    title: 'Equipment knowledge base',
    description:
      'Manuals and datasheets staged for the chat assistant knowledge base. Invisible to operators until published.',
    approveLabel: 'Approve and publish',
    rejectable: true,
  },
  tariff: {
    title: 'Utility tariff',
    description:
      'Tariff extraction is not automated yet; the document is queued for manual entry after onboarding completes.',
    approveLabel: 'Acknowledge',
    rejectable: false,
  },
};

const STATUS_TAG: Record<string, { type: 'blue' | 'green' | 'red' | 'cool-gray'; label: string }> =
  {
    pending: { type: 'blue', label: 'Staged' },
    done: { type: 'green', label: 'Published' },
    failed: { type: 'red', label: 'Failed' },
    rejected: { type: 'cool-gray', label: 'Rejected' },
  };

interface PublishTargetCardProps {
  action: PublishAction;
  /** Document ids routed to this target (from documents_by_target). */
  documents: string[];
  decision: TargetDecision | undefined;
  onDecide: (_target: string, _decision: TargetDecision) => void;
  disabled: boolean;
}

/**
 * One publish target in the review step. Pending targets take a decision
 * (latched visually until applied); done targets lock (the service rejects
 * decisions on published targets); failed targets show the error and stay
 * decidable so a re-apply covers only unresolved work; rejected targets
 * report that their items returned to the checklist.
 */
export default function PublishTargetCard({
  action,
  documents,
  decision,
  onDecide,
  disabled,
}: PublishTargetCardProps) {
  const copy = TARGET_COPY[action.target] ?? {
    title: action.target,
    description: 'Staged results for this target.',
    approveLabel: 'Approve',
    rejectable: true,
  };
  const statusTag = STATUS_TAG[action.status] ?? {
    type: 'cool-gray' as const,
    label: action.status,
  };
  const decidable = !disabled && (action.status === 'pending' || action.status === 'failed');

  return (
    <Tile className="obw-target-card">
      <div className="obw-target-header">
        <h5>{copy.title}</h5>
        <Tag type={statusTag.type} size="sm">
          {statusTag.label}
        </Tag>
      </div>
      <p className="obw-target-description">{copy.description}</p>
      {action.external_ref && <p className="obw-target-ref">Reference: {action.external_ref}</p>}
      {documents.length > 0 && (
        <p className="obw-target-docs">
          {documents.length} document{documents.length === 1 ? '' : 's'} routed to this target
        </p>
      )}
      {action.status === 'failed' && (
        <InlineNotification
          kind="error"
          title="Publish failed"
          subtitle={
            action.detail ?? `Attempt ${action.attempts} failed; applying decisions retries it.`
          }
          lowContrast
          hideCloseButton
        />
      )}
      {action.status === 'rejected' && (
        <p className="obw-target-note">
          Rejected — its documents returned to review and the session to collecting.
        </p>
      )}
      {(action.status === 'pending' || action.status === 'failed') && (
        <div className="obw-target-actions">
          <Button
            size="sm"
            kind={decision === 'approve' ? 'primary' : 'tertiary'}
            disabled={!decidable}
            onClick={() => onDecide(action.target, 'approve')}
          >
            {copy.approveLabel}
          </Button>
          {copy.rejectable && (
            <Button
              size="sm"
              kind={decision === 'reject' ? 'danger' : 'ghost'}
              disabled={!decidable}
              onClick={() => onDecide(action.target, 'reject')}
            >
              Reject
            </Button>
          )}
        </div>
      )}
    </Tile>
  );
}
